import { cardCommentsSchema } from "@common/utils/redline/wire/redline-payload";

import { cardCommentKeyOf } from "../components/chat/proposal-card/card-comment-selections";
import { type ChatMessage, getActionIdentifier } from "../types/chat";
import type { ProposalEdit, ProposalEditsByAction } from "./proposal-edits-storage";

function toolResultObjectOf(content: string): Record<string, unknown> | null {
    try {
        const parsed: unknown = JSON.parse(content);
        return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
    } catch {
        return null;
    }
}

function editedToolResultOf(toolResult: Record<string, unknown>, edit: ProposalEdit): Record<string, unknown> | null {
    if (edit.kind === "comment") return "text" in toolResult ? { ...toolResult, text: edit.text } : null;
    const isEditCard = "redline" in toolResult || "ooxml" in toolResult;
    if (!isEditCard) return null;
    const { change_throughout: _placesNoLongerChooseTheText, ...toolResultOfTheEditedText } = toolResult;
    return { ...toolResultOfTheEditedText, redline: edit.textRedline, ooxml: [] };
}

function withCardCommentEdits(toolResult: Record<string, unknown>, cardId: string, edits: ProposalEditsByAction): Record<string, unknown> {
    const comments = cardCommentsSchema.safeParse(toolResult.comments);
    if (!comments.success || comments.data.length === 0) return toolResult;
    const commentEdits = comments.data.map((cardComment) => {
        const cardCommentKey = cardCommentKeyOf(cardId, cardComment);
        return cardCommentKey === null ? undefined : edits.get(cardCommentKey);
    });
    if (commentEdits.every((edit) => edit?.kind !== "comment")) return toolResult;
    const editedComments = comments.data.map((cardComment, commentPosition) => {
        const edit = commentEdits[commentPosition];
        if (edit?.kind !== "comment") return cardComment;
        return { ...cardComment, text: edit.text };
    });
    return { ...toolResult, comments: editedComments };
}

export function withProposalEdits(messages: ChatMessage[], edits: ProposalEditsByAction): ChatMessage[] {
    if (edits.size === 0) return messages;
    return messages.map((message, index) => {
        if (message.messageType !== "tool_result") return message;
        const cardId = getActionIdentifier(message, `msg-${index}`);
        const toolResult = toolResultObjectOf(message.content);
        if (toolResult === null) return message;
        const edit = edits.get(cardId);
        const editedCard = edit === undefined ? toolResult : editedToolResultOf(toolResult, edit) ?? toolResult;
        const edited = withCardCommentEdits(editedCard, cardId, edits);
        return edited === toolResult ? message : { ...message, content: JSON.stringify(edited) };
    });
}
